import { css } from "@emotion/react";
import { format, parseISO } from "date-fns";
import {
  Message as IMessage,
  compareMessagesByDateTimeAndId,
  iDialogMessagesFile,
  normalizeHistoryMessages
} from "../use-omnichannel";
import MessageButton, { Message, StatusMessage } from "./message";
import { useAppSelector } from "../../redux/hooks";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import OmnichannelService from "../../services/omnichannelService";
import { useToast } from "../../components/toast-provider";
import { AuthContextType, useAuth } from "../../auth/auth-provider";

export interface MessagesProps {
  dialog: IMessage[];
  dialogInfo: iDialogMessagesFile;
  shouldScroll: boolean;
  onShouldScroll: () => void;
  messageRef: React.RefObject<HTMLDivElement>;
  deletedParentIds: Set<number>;
  onMessageDeleted: (id: number) => void;
  onRetryMessage: (clientTempId: string) => void;
  onForwardMessage: (
    messageId: number,
    text: string,
    contactIds: number[],
    sourceDialogId: number
  ) => void;
}

type QuotedMessage = {
  parentId: number;
  message: string;
};

const PAGE_SIZE = 20;
const MAX_RENDERED_MESSAGES = 60;
const SCROLL_EDGE_THRESHOLD = 8;

const keepOldestMessages = (messages: IMessage[], limit: number) => {
  if (messages.length <= limit) {
    return messages;
  }

  return messages.slice(0, limit);
};

const keepNewestMessages = (messages: IMessage[], limit: number) => {
  if (messages.length <= limit) {
    return messages;
  }

  return messages.slice(messages.length - limit);
};

const trimByWindowMode = (
  messages: IMessage[],
  mode: "recent" | "older",
  limit: number
) => {
  if (messages.length <= limit) {
    return messages;
  }

  return mode === "older"
    ? keepOldestMessages(messages, limit)
    : keepNewestMessages(messages, limit);
};

const Messages = ({
  dialog,
  dialogInfo,
  messageRef,
  deletedParentIds,
  onMessageDeleted,
  onRetryMessage,
  onForwardMessage
}: MessagesProps) => {
  const { user } = useAuth() as AuthContextType;
  const { findDialogHistoryMessages } = OmnichannelService();
  const [allMessages, setAllMessages] = useState(
    normalizeHistoryMessages(dialog || [], dialogInfo)
  );
  const [hasOlder, setHasOlder] = useState(
    normalizeHistoryMessages(dialog || [], dialogInfo).length >= PAGE_SIZE
  );
  const [hasNewer, setHasNewer] = useState(false);
  const toast = useToast();
  const loadingRef = useRef(false);
  const activeDialogIdRef = useRef(dialogInfo.id);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const windowModeRef = useRef<"recent" | "older">("recent");
  const pendingLayoutRef = useRef<{
    direction: "older" | "newer";
    prevScrollTop: number;
    addedTopCount: number;
    removedTopHeight: number;
  } | null>(null);
  const initialScrollPendingRef = useRef(true);
  const edgeLoadingEnabledRef = useRef(false);
  const contactId = useAppSelector((state) => state.omnichannel.contactId);
  const canQuoteCurrentDialog =
    dialogInfo.transferred && dialogInfo.userTransferredId === user.id;

  const measureTopHeight = (count: number) => {
    const container = containerRef.current;
    if (!container || count <= 0) {
      return 0;
    }

    return Array.from(container.children)
      .slice(0, count)
      .reduce((sum, child) => sum + (child as HTMLElement).offsetHeight, 0);
  };

  const loadMessages = async (direction: "older" | "newer") => {
    if (loadingRef.current) return;

    if (direction === "older" && !hasOlder) return;
    if (direction === "newer" && !hasNewer) return;

    const cursorMessage =
      direction === "older"
        ? allMessages[0]
        : allMessages[allMessages.length - 1];

    if (!cursorMessage) {
      if (direction === "older") {
        setHasOlder(false);
      } else {
        setHasNewer(false);
      }
      return;
    }

    loadingRef.current = true;
    const requestDialogId = dialogInfo.id;
    const container = containerRef.current;
    const prevScrollTop = container?.scrollTop ?? 0;
    const currentMessagesSnapshot = allMessages;

    try {
      const messages = await findDialogHistoryMessages(dialogInfo.id, {
        limit: PAGE_SIZE,
        cursorMessageId: cursorMessage.id,
        cursorDialogId: cursorMessage.sourceDialogId ?? dialogInfo.id,
        direction
      });

      if (requestDialogId !== activeDialogIdRef.current) {
        return;
      }

      if (!messages || messages.length === 0) {
        if (direction === "older") {
          setHasOlder(false);
        } else {
          setHasNewer(false);
          windowModeRef.current = "recent";
        }
        return;
      }

      const normalizedIncoming = normalizeHistoryMessages(messages, dialogInfo);
      const seenMessages = new Set(currentMessagesSnapshot.map((m) => m.id));
      const onlyNew = normalizedIncoming.filter(
        (message) => !seenMessages.has(message.id)
      );
      if (onlyNew.length === 0) {
        pendingLayoutRef.current = null;
        if (direction === "older") {
          setHasOlder(messages.length >= PAGE_SIZE);
        } else {
          setHasNewer(messages.length >= PAGE_SIZE);
          if (messages.length < PAGE_SIZE) {
            windowModeRef.current = "recent";
          }
        }
        return;
      }

      const trimCountForNewer =
        direction === "newer"
          ? Math.max(
              0,
              currentMessagesSnapshot.length +
                onlyNew.length -
                MAX_RENDERED_MESSAGES
            )
          : 0;

      pendingLayoutRef.current = {
        direction,
        prevScrollTop,
        addedTopCount: direction === "older" ? onlyNew.length : 0,
        removedTopHeight:
          direction === "newer" ? measureTopHeight(trimCountForNewer) : 0
      };

      setAllMessages((prev) => {
        const seen = new Set(prev.map((m) => m.id));
        const uniqueMessages = normalizedIncoming.filter(
          (message) => !seen.has(message.id)
        );
        if (uniqueMessages.length === 0) {
          return prev;
        }

        if (direction === "older") {
          const next = [...uniqueMessages, ...prev];
          return keepOldestMessages(next, MAX_RENDERED_MESSAGES);
        }

        const next = [...prev, ...uniqueMessages];
        return keepNewestMessages(next, MAX_RENDERED_MESSAGES);
      });

      if (direction === "older") {
        setHasOlder(messages.length >= PAGE_SIZE);
        if (
          currentMessagesSnapshot.length + onlyNew.length >
          MAX_RENDERED_MESSAGES
        ) {
          setHasNewer(true);
        }
        windowModeRef.current = "older";
      } else {
        setHasNewer(messages.length >= PAGE_SIZE);
        if (trimCountForNewer > 0) {
          setHasOlder(true);
        }
        windowModeRef.current =
          messages.length < PAGE_SIZE ? "recent" : "older";
      }
    } catch {
      pendingLayoutRef.current = null;
      toast.current?.show({
        severity: "warn",
        summary: "Atenção",
        detail:
          direction === "older"
            ? "Erro ao carregar mensagens anteriores"
            : "Erro ao carregar mensagens mais recentes",
        life: 3000
      });
    } finally {
      loadingRef.current = false;
    }
  };

  useEffect(() => {
    const initialMessages = normalizeHistoryMessages(dialog || [], dialogInfo);
    activeDialogIdRef.current = dialogInfo.id;
    loadingRef.current = false;
    pendingLayoutRef.current = null;
    windowModeRef.current = "recent";
    initialScrollPendingRef.current = true;
    edgeLoadingEnabledRef.current = false;
    setAllMessages(initialMessages);
    setHasOlder(initialMessages.length >= PAGE_SIZE);
    setHasNewer(false);
  }, [dialogInfo.id]);

  useEffect(() => {
    if (!dialog || dialog.length === 0) return;

    setAllMessages((prev) => {
      const byId = new Map<number, IMessage>();
      const normalizedMessages = normalizeHistoryMessages(dialog, dialogInfo);

      for (const m of prev) {
        if (m.sendState) {
          continue;
        }
        byId.set(m.id, m);
      }

      for (const m of normalizedMessages) {
        byId.set(m.id, m);
      }

      const merged = Array.from(byId.values()).sort(
        compareMessagesByDateTimeAndId
      );

      return trimByWindowMode(
        merged,
        windowModeRef.current,
        MAX_RENDERED_MESSAGES
      );
    });
  }, [dialog, dialogInfo]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (!edgeLoadingEnabledRef.current) {
      return;
    }

    const el = e.currentTarget;
    const isNearTop = el.scrollTop <= SCROLL_EDGE_THRESHOLD;
    const isNearBottom =
      el.scrollHeight - el.scrollTop - el.clientHeight <= SCROLL_EDGE_THRESHOLD;

    if (isNearTop) {
      loadMessages("older");
      return;
    }

    if (isNearBottom) {
      loadMessages("newer");
    }
  };

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    if (initialScrollPendingRef.current) {
      container.scrollTop = container.scrollHeight;
      initialScrollPendingRef.current = false;
      window.requestAnimationFrame(() => {
        edgeLoadingEnabledRef.current = true;
      });
      return;
    }

    const pendingLayout = pendingLayoutRef.current;
    if (!pendingLayout) return;

    if (pendingLayout.direction === "older") {
      const addedTopHeight = measureTopHeight(pendingLayout.addedTopCount);
      container.scrollTop = pendingLayout.prevScrollTop + addedTopHeight;
    } else if (pendingLayout.removedTopHeight > 0) {
      container.scrollTop = Math.max(
        0,
        pendingLayout.prevScrollTop - pendingLayout.removedTopHeight
      );
    }

    pendingLayoutRef.current = null;
  }, [allMessages]);

  return (
    <div
      ref={containerRef}
      onScroll={handleScroll}
      css={css`
        padding: 2rem;
        display: flex;
        flex: 1;
        flex-direction: column;
        overflow: auto;

        &&::-webkit-scrollbar {
          width: 5px;
          background-color: transparent;
          border-radius: 4px;
        }

        &&::-webkit-scrollbar-thumb {
          background-color: #c1c1c1;
          border-radius: 4px;
        }

        @media screen and (max-width: 599px) {
          padding: 0;
        }
      `}
    >
      {allMessages?.map((m, index) => {
        const dateTime = parseISO(m?.dateTime);
        const updatedAt = m.updatedAt ? parseISO(m.updatedAt) : undefined;
        const quotedMessage = (m as any)?.quotedMessage as
          | QuotedMessage
          | undefined;
        const sourceDialogId = m.sourceDialogId ?? dialogInfo.id;
        const sourceDialogDateTime =
          m.sourceDialogDateTime ?? dialogInfo.dateTime;
        const sourceDialogName = m.sourceDialogName ?? dialogInfo.name;
        const sourceBotName = m.sourceBotName ?? dialogInfo.botName;
        const sourceUserName = m.sourceUserName;
        const previousMessage = index > 0 ? allMessages[index - 1] : undefined;
        const previousSourceDialogId =
          previousMessage?.sourceDialogId ?? dialogInfo.id;
        const shouldRenderStatus =
          index === 0 || previousSourceDialogId !== sourceDialogId;
        const isHistoricalMessage = sourceDialogId !== dialogInfo.id;
        return (
          <div ref={messageRef} key={m.id}>
            {contactId < 1 && (
              <>
                {shouldRenderStatus && sourceDialogDateTime ? (
                  <StatusMessage
                    message={`Conversa nº ${sourceDialogId} - ${format(
                      parseISO(sourceDialogDateTime),
                      "dd/MM/yyyy HH:mm"
                    )}`}
                  />
                ) : null}

                <Message
                  id={m.id}
                  dialogId={sourceDialogId}
                  isHistoricalMessage={isHistoricalMessage}
                  canQuoteMessage={canQuoteCurrentDialog}
                  date={dateTime}
                  updatedAt={updatedAt}
                  status={m.status}
                  forwardedMessage={Boolean(m.forwardedMessage)}
                  reply={m.sender !== "CLIENT"}
                  isHumanResponder={m.sender === "HUMAN"}
                  humanName={sourceUserName}
                  robotName={sourceBotName}
                  chatName={sourceDialogName}
                  message={m.message}
                  files={m.files}
                  quotedMessage={quotedMessage}
                  sender={m.sender}
                  deletedParentIds={deletedParentIds}
                  onMessageDeleted={onMessageDeleted}
                  sendState={m.sendState}
                  errorText={m.errorText}
                  onRetry={
                    m.clientTempId
                      ? () => onRetryMessage(m.clientTempId as string)
                      : undefined
                  }
                  onForwardMessage={onForwardMessage}
                />

                <div
                  css={css`
                    display: flex;
                    flex-direction: column;
                    gap: 10px;
                    padding: 0px 20px 10px 20px;
                  `}
                >
                  {m.buttons
                    ? m.buttons.map((b) => (
                        <MessageButton key={b.id} label={b.label} />
                      ))
                    : null}
                </div>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default Messages;
